import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { toast } from "@/components/ui/use-toast";
import { Supplier, SupplierFormData } from '@/types/procurement';

export const useSuppliers = () => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all suppliers
  const fetchSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('suppliers')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setSuppliers(data || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch suppliers';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new supplier
  const createSupplier = useCallback(async (supplierData: SupplierFormData): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      // Check for duplicate code
      const { data: existingSupplier } = await supabase
        .from('suppliers')
        .select('code')
        .eq('code', supplierData.code.toUpperCase())
        .single();

      if (existingSupplier) {
        toast({
          title: 'Duplicate Code',
          description: `Supplier code ${supplierData.code.toUpperCase()} already exists`,
          variant: 'destructive',
        });
        return false;
      }

      const { error } = await supabase
        .from('suppliers')
        .insert([{
          ...supplierData,
          code: supplierData.code.toUpperCase(),
          type: 'supplier', // Default type
        }]);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Supplier created successfully',
      });

      // Refresh suppliers list
      await fetchSuppliers();
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create supplier';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchSuppliers]);

  // Update supplier
  const updateSupplier = useCallback(async (id: string, supplierData: SupplierFormData): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      // Check for duplicate code (excluding current supplier)
      const { data: existingSupplier } = await supabase
        .from('suppliers')
        .select('code')
        .eq('code', supplierData.code.toUpperCase())
        .neq('id', id)
        .single();

      if (existingSupplier) {
        toast({
          title: 'Duplicate Code',
          description: `Supplier code ${supplierData.code.toUpperCase()} already exists`,
          variant: 'destructive',
        });
        return false;
      }

      const { error } = await supabase
        .from('suppliers')
        .update({
          ...supplierData,
          code: supplierData.code.toUpperCase(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Supplier updated successfully',
      });

      // Refresh suppliers list
      await fetchSuppliers();
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update supplier';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchSuppliers]);

  // Delete supplier
  const deleteSupplier = useCallback(async (id: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const { error } = await supabase
        .from('suppliers')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Supplier deleted successfully',
      });

      // Refresh suppliers list
      await fetchSuppliers();
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete supplier';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchSuppliers]);

  // Toggle supplier active status
  const toggleSupplierStatus = useCallback(async (id: string, is_active: boolean): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const { error } = await supabase
        .from('suppliers')
        .update({ 
          is_active,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Supplier ${is_active ? 'activated' : 'deactivated'} successfully`,
      });

      // Refresh suppliers list
      await fetchSuppliers();
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update supplier status';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchSuppliers]);

  // Get active suppliers for dropdowns
  const getActiveSuppliers = useCallback(() => {
    return suppliers.filter(supplier => supplier.is_active);
  }, [suppliers]);

  // Load suppliers on hook initialization
  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  return {
    suppliers,
    loading,
    error,
    fetchSuppliers,
    createSupplier,
    updateSupplier,
    deleteSupplier,
    toggleSupplierStatus,
    getActiveSuppliers,
  };
};
